/**
 * /operations/eos/calculation-drafts — Session 3B PR 7.
 *
 * Owning agent: Rawatib. HR scratch-pad for what-if EOS scenarios.
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { EosCalculationDraftItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function EosCalculationDraftsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-eos-drafts", legalEntityId],
    queryFn: () => session3bApi.listEosCalculationDrafts(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: EosCalculationDraftItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const sumExposure = items.reduce((a, r) => a + r.calculated_settlement_sar, 0);
    const perEmp = new Map<string, number>();
    for (const r of items) {
      perEmp.set(r.employee_id, (perEmp.get(r.employee_id) ?? 0) + 1);
    }
    const noisiest = Array.from(perEmp.entries()).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      attention: 0,
      critical: 0,
      sumExposure,
      uniqueEmployees: perEmp.size,
      noisiest_employee_id: noisiest?.[0],
      noisiest_count: noisiest?.[1] ?? 0,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("eos.calculation_drafts.title", "EOS Calculation Drafts")}
          subtitle={t(
            "eos.calculation_drafts.subtitle",
            "HR scratch-pad for what-if EOS scenarios. Calculate, save, share — then convert the chosen scenario into a real Termination workflow.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-tertiary">
            {t(
              "eos.calculation_drafts.hint",
              "Drafts don't trigger M18 Termination — they live until you convert one. Rawatib computes each scenario from the live employee record.",
            )}
          </p>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "eos.calculation_drafts.cta.new_note",
                  "New EOS Calculation drawer: employee picker + termination-date scenario + reason_code dropdown → Calculate (engine) → Save as Draft. The Calculate-via-M18-engine wiring is a separate workstream.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("eos.calculation_drafts.cta.new", "New EOS Calculation")}
          </button>
        </div>

        {counts.noisiest_count >= 2 && counts.noisiest_employee_id && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<Sparkles className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "eos.calculation_drafts.suggestion.repeat_headline",
              `${counts.noisiest_count} drafts for one employee — decision needed?`,
            )}
            body={t(
              "eos.calculation_drafts.suggestion.repeat_body",
              "Rawatib pattern: multiple unsubmitted drafts usually signal HR weighing options. Convert the chosen one to a Termination workflow.",
            )}
            primaryAction={{
              label: t("eos.calculation_drafts.suggestion.open_employee", "Open employee"),
              onClick: () =>
                window.location.assign(`/people/employees/${counts.noisiest_employee_id}`),
            }}
            suggestionKey={`eos-draft-repeat-${counts.noisiest_employee_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<Sparkles className="h-4 w-4" />}
            severity="info"
            headline={t(
              "eos.calculation_drafts.suggestion.empty_headline",
              "No EOS drafts in flight.",
            )}
            body={t(
              "eos.calculation_drafts.suggestion.empty_body",
              "Rawatib notes: when you need to model a separation scenario, use + New EOS Calculation. Drafts are private until you share.",
            )}
            primaryAction={{
              label: t("eos.calculation_drafts.suggestion.open_eos_list", "Active separations"),
              onClick: () => window.location.assign("/operations/eos"),
            }}
            suggestionKey="eos-draft-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.scenario_date", "Termination scenario")}</th>
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.reason", "Reason scenario")}</th>
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.settlement", "Settlement (SAR)")}</th>
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.saved", "Saved")}</th>
                  <th className="px-3 py-2">{t("eos.calculation_drafts.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.draft_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.termination_date_scenario}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.reason_code_scenario}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "eos.calculation_drafts.trace.settlement",
                              `Settlement scenario = engine output of the M18 EOS rule for this reason_code. Includes Article 84/85 gratuity, accrued leave encashment, outstanding loan deductions. Re-runs against live employee record at view time.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{_fmtSar(row.calculated_settlement_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-tertiary">
                      <div>{row.saved_by_name}</div>
                      <div>
                        <span dir="ltr">{row.saved_at}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "eos.calculation_drafts.action.convert_note",
                                "Convert to Termination — initiates the M18 workflow with this draft's parameters.",
                              ),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          {t("eos.calculation_drafts.action.convert", "Convert")}
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("eos.calculation_drafts.title", "EOS Calculation Drafts"),
            window_label: `${counts.uniqueEmployees} employees · est SAR ${_fmtSar(counts.sumExposure)}`,
          }}
        />
      </aside>
    </div>
  );
}
